<script setup lang="ts">
import {
  Building2,
  FilePenLine,
  // MessageSquare,
  Settings,
  X,
} from 'lucide-vue-next';
import type { Component } from 'vue';
import { useRouter } from 'vue-router';
import useAppStore from '@/stores/modules/app';

const appStore = useAppStore();
const router = useRouter();

// 移动端点击导航项后关闭 sidebar
router.afterEach(() => {
  appStore.closeSidebar();
});

interface NavItem {
  title: string
  to: string
}

interface NavGroup {
  title: string
  icon: Component
  items: NavItem[]
}

const navigation: NavGroup[] = [
  {
    title: '创作',
    icon: FilePenLine,
    items: [
      { title: '文章管理', to: '/settings/article' },
      { title: '收藏列表', to: '/settings/favorite' },
    ],
  },
  {
    title: '管理',
    icon: Building2,
    items: [
      { title: '社区管理', to: '/settings/community' },
      { title: '添加仓库', to: '/settings/repository' },
      { title: '添加组织', to: '/settings/organization' },
    ],
  },
  // {
  //   title: '消息中心',
  //   icon: MessageSquare,
  //   items: [
  //     { title: '我的消息', to: '/settings/message' },
  //     { title: '历史评论', to: '/settings/comment' },
  //   ],
  // },
  {
    title: '设置',
    icon: Settings,
    items: [
      { title: '个人信息', to: '/settings/profile' },
      // { title: '通知设置', to: '/settings/notification' },
    ],
  },
]
</script>

<template>
  <!-- 宽屏：静态侧边栏 -->
  <nav class="flex w-64 shrink-0 flex-col gap-6 py-6">
    <div v-for="(group, index) in navigation" :key="index" class="flex flex-col gap-2">
      <div class="flex items-center gap-2 border-b border-border px-3 pb-2">
        <component :is="group.icon" class="size-4 text-muted-foreground" />
        <h3 class="text-sm text-muted-foreground font-medium">
          {{ group.title }}
        </h3>
      </div>
      <div class="flex flex-col gap-1">
        <RouterLink
          v-for="item in group.items"
          :key="item.to"
          :to="item.to"
          class="relative flex items-center px-4 py-2 text-sm text-foreground font-medium transition-colors duration-200 hover:bg-accent hover:text-accent-foreground"
          active-class="bg-accent text-primary font-semibold before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-primary before:content-['']"
        >
          {{ item.title }}
        </RouterLink>
      </div>
    </div>
  </nav>

  <!-- 窄屏：抽屉式侧边栏 -->
  <Teleport to="body">
    <!-- 遮罩 -->
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="appStore.sidebarOpen"
        class="md:hidden fixed inset-0 z-40 bg-black/40"
        @click="appStore.closeSidebar"
      ></div>
    </Transition>

    <!-- 抽屉 -->
    <Transition
      enter-active-class="transition-transform duration-[250ms] ease-out"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-[250ms] ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <nav
        v-if="appStore.sidebarOpen"
        class="md:hidden fixed inset-y-0 left-0 z-50 w-64 flex flex-col gap-6 bg-background px-4 py-6 shadow-xl overflow-y-auto border-r border-border"
      >
        <!-- 关闭按钮 -->
        <div class="flex justify-end">
          <button
            class="flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="关闭导航菜单"
            @click="appStore.closeSidebar"
          >
            <X class="size-5" />
          </button>
        </div>

        <div v-for="(group, index) in navigation" :key="index" class="flex flex-col gap-2">
          <div class="flex items-center gap-2 border-b border-border px-3 pb-2">
            <component :is="group.icon" class="size-4 text-muted-foreground" />
            <h3 class="text-sm text-muted-foreground font-medium">
              {{ group.title }}
            </h3>
          </div>
          <div class="flex flex-col gap-1">
            <RouterLink
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="relative flex items-center px-4 py-2 text-sm text-foreground font-medium transition-colors duration-200 hover:bg-accent hover:text-accent-foreground"
              active-class="bg-accent text-primary font-semibold before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-primary before:content-['']"
            >
              {{ item.title }}
            </RouterLink>
          </div>
        </div>
      </nav>
    </Transition>
  </Teleport>
</template>
